import React from 'react';

import { Box, Button, Grid } from '@mui/material';
import { DateTime } from 'luxon';
import { FormSpy } from 'react-final-form';
import { useNavigate } from 'react-router';

import { DeepPartial } from '@hrlogics/helper-types';
import { Field, Form, FormApi } from '@hrlogics/react-components/form';

import {
  appRoutes,
  DateFormatPatterns,
  DEFAULT_VALUE_IF_NIL,
  RouterEntitiesId,
  SSN_MASK,
} from '@clearverify/constants';

import { InboxItemsScrollableListDocument, useCreateVerificationRequestMutation } from 'src/api';
import { AutocompleteField, DateField, MaskedTextField, TextField } from 'src/components/finalForm';
import {
  cvStaffVerificationsRequestTypeOptions,
  VerificationRequestCreateFormFields,
} from 'src/constants/verificationRequest';
import { useNotification } from 'src/hooks/app';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';
import { useVerifierAutocomplete } from 'src/hooks/verifier/useVerifierAutocomplete';
import { convertEmployerListItemsToEmployerDataItems } from 'src/utils/employer';
import { buildUrl } from 'src/utils/router';
import { validateWithYupSchema } from 'src/utils/yup';

import { VerificationRequestCreateByVerifierModalFormConsumerErrorBox } from '../VerificationRequestCreateByVerifierModalFormConsumerErrorBox';
import { VerificationRequestCreateByVerifierModalFormErrorBox } from '../VerificationRequestCreateByVerifierModalFormErrorBox';
import { VerificationRequestCreateFormPermissiblePurposeField } from '../VerificationRequestCreateFormPermissiblePurposeField';
import { VerificationRequestCreateFormVerificationReportTypeField } from '../VerificationRequestCreateFormVerificationReportTypeField';
import { VerificationRequestCreateModalFormEmployerField } from '../VerificationRequestCreateModalFormEmployerField';

import {
  VerificationRequestCreateByCVStaffFormFields,
  VerificationRequestCreateFormValues,
} from './types';
import { verificationRequestCreateValidationSchema } from './utils';

interface VerificationRequestCreateByCvStaffModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const VerificationRequestCreateByCvStaffModalForm: React.FC<
  VerificationRequestCreateByCvStaffModalFormProps
> = ({ closeModal, switchClosability }) => {
  const navigate = useNavigate();

  const { createApolloContext } = useNotification();

  const { isFormInfoLoading, resetData, startSubmitting, stopSubmitting } =
    useVerificationRequestFormData();

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset data on unmount only
  React.useEffect(() => {
    return () => resetData();
  }, []);

  const verifierAutocompleteOptions = useVerifierAutocomplete();

  const verifierId = verifierAutocompleteOptions.options[0]?.id || DEFAULT_VALUE_IF_NIL;
  const verifierAutocompleteOptionsLoading = verifierAutocompleteOptions.loading;

  const [createVerificationRequest] = useCreateVerificationRequestMutation({
    context: createApolloContext({
      errorMessage: 'Error creating verification request',
      successMessage: 'Verification request successfully created',
    }),
    refetchQueries: [InboxItemsScrollableListDocument],
  });

  const isDisabledPermissiblePurpose = verifierAutocompleteOptions.options.length === 0;

  const minDate = React.useMemo(() => DateTime.fromISO('1800-01-01'), []);
  const maxDate = React.useRef(DateTime.now());

  const onSubmit = React.useCallback(
    async (
      formValues: VerificationRequestCreateFormValues,
      formApi: FormApi<
        VerificationRequestCreateFormValues,
        DeepPartial<VerificationRequestCreateFormValues>
      >,
    ) => {
      formApi.change(VerificationRequestCreateFormFields.error, null);

      const employeeDateOfBirth = DateTime.fromISO(formValues.employeeDateOfBirth);
      if (!employeeDateOfBirth.isValid) return;

      startSubmitting();
      switchClosability(false);

      let success = false;
      let inboxItemIdToRedirect: string | null = null;

      try {
        const { data } = await createVerificationRequest({
          variables: {
            verifierId: formValues.verifier.id,
            ssn: formValues.ssn,
            employeeBirthDate: employeeDateOfBirth.toISODate(),
            employeeFirstName: formValues.employeeFirstName,
            employeeLastName: formValues.employeeLastName,
            requestType: formValues.verificationReportType,
            permissiblePurposeId: formValues.permissiblePurposeId,
            noteToEmployee: formValues.noteToEmployee,
            companies: convertEmployerListItemsToEmployerDataItems(formValues.employers),
          },
        });

        success = true;

        if (data?.createVerificationRequest.inboxItemId) {
          inboxItemIdToRedirect = data.createVerificationRequest.inboxItemId;
        }

        switchClosability(true);
      } finally {
        stopSubmitting();
        switchClosability(true);
        if (success) {
          closeModal();
        }
      }

      if (inboxItemIdToRedirect) {
        navigate(
          buildUrl(appRoutes.inbox.inboxItem, {
            [RouterEntitiesId.inboxItem]: inboxItemIdToRedirect,
          }),
        );
      }
    },
    [
      closeModal,
      createVerificationRequest,
      startSubmitting,
      stopSubmitting,
      navigate,
      switchClosability,
    ],
  );

  return (
    <Form<VerificationRequestCreateFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      subscription={{ valid: true, values: true, submitting: true, errors: true }}
      containerProps={{ mt: 0 }}
      validate={validateWithYupSchema(verificationRequestCreateValidationSchema)}
    >
      {({ submitting, valid, values, form }) => {
        return (
          <React.Fragment>
            <Grid container spacing={2.5}>
              <Grid size={12}>
                <Field
                  name={VerificationRequestCreateByCVStaffFormFields.verifier}
                  component={AutocompleteField}
                  label="Verifier"
                  fullWidth
                  freeSolo={false}
                  placeholder="Search verifier by name"
                  required
                  {...verifierAutocompleteOptions}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  name={VerificationRequestCreateFormFields.ssn}
                  component={MaskedTextField}
                  label="SSN"
                  mask={SSN_MASK}
                  fullWidth
                  required
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeFirstName}
                  component={TextField}
                  label="Employee First Name"
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeLastName}
                  component={TextField}
                  label="Employee Last Name"
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeDateOfBirth}
                  component={DateField}
                  minDate={minDate}
                  maxDate={maxDate.current}
                  inputFormat={DateFormatPatterns.shortDateWithSlash}
                  defaultValue={DateFormatPatterns.shortDateWithSlash}
                  label="Employee Date of Birth"
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <VerificationRequestCreateModalFormEmployerField />
              </Grid>

              <Grid size={12}>
                <VerificationRequestCreateFormVerificationReportTypeField
                  options={cvStaffVerificationsRequestTypeOptions}
                  disableWhenIncomeUnavailable={false}
                />
              </Grid>

              <Grid size={12}>
                <FormSpy<VerificationRequestCreateFormValues>
                  onChange={formState => {
                    if (formState.values.verifier !== values.verifier) {
                      form.change(
                        VerificationRequestCreateFormFields.permissiblePurposeId,
                        undefined,
                      );
                    }
                  }}
                />

                <VerificationRequestCreateFormPermissiblePurposeField
                  verifierId={verifierId}
                  disabled={isDisabledPermissiblePurpose || verifierAutocompleteOptionsLoading}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  name={VerificationRequestCreateFormFields.noteToEmployee}
                  component={TextField}
                  label="Note to Employee (optional)"
                  fullWidth
                />
              </Grid>
            </Grid>
            <VerificationRequestCreateByVerifierModalFormErrorBox />
            <VerificationRequestCreateByVerifierModalFormConsumerErrorBox />
            <Box display="flex" gap={2} mt={4} justifyContent="center">
              <Button
                variant="outlined"
                color="error"
                onClick={() => closeModal()}
                disabled={submitting}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting}
                loading={submitting || isFormInfoLoading}
              >
                Submit request
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};

const initialValues: Partial<VerificationRequestCreateFormValues> = {
  [VerificationRequestCreateFormFields.verificationReportType]:
    cvStaffVerificationsRequestTypeOptions.at(0)?.value,
  [VerificationRequestCreateFormFields.employers]: [],
};
